// src/pages/course/GetCoursePage.js
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

import BackButton from "../../components/common/BackButton";
import CourseMapView from "../../components/course/CourseMapView";
import CourseDetailHeader from "../../components/course/CourseDetailHeader";
import ReviewHeader from "../../components/course/ReviewHeader";
import ReviewContent from "../../components/course/ReviewContent";

import {
  getCourseDetail,
  publicCourse,
  useCourse as startCourseApi,
  deleteReviewApi,
  checkAddReviewPermission,
  checkUpdateReviewPermission,
  getUsingWorkRecord,
} from "../../api/courseApi";
import { useJWT } from "../../contexts/JWTContext";
// [변경] Toast Context 임포트
import { useToast } from "../../contexts/ToastContext";

function GetCoursePage({ courseId: propCourseId }) {
  const { courseId: paramCourseId } = useParams();
  const navigate = useNavigate();
  const { userId: loginUserId } = useJWT();
  // [변경] useToast 사용
  const { showToast } = useToast();

  const courseId = paramCourseId || propCourseId;

  const [course, setCourse] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);

  const [usingWorkRecordId, setUsingWorkRecordId] = useState(null);
  const [checkingUsing, setCheckingUsing] = useState(false);

  async function fetchCourseDetail() {
    if (!courseId) return;
    try {
      setLoading(true);
      const data = await getCourseDetail(courseId);
      setCourse(data.course || data);
      setReviews(data.reviews || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchCourseDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  useEffect(() => {
    if (!courseId || !loginUserId) return;

    let cancelled = false;

    const checkUsing = async () => {
      try {
        setCheckingUsing(true);
        const resp = await getUsingWorkRecord(courseId, loginUserId);
        if (cancelled) return;

        const id = resp?.workRecordId ?? null;
        setUsingWorkRecordId(id);
      } catch (e) {
        // 없으면(404 등) -> 진행중 없음으로 처리
        if (!cancelled) setUsingWorkRecordId(null);
      } finally {
        if (!cancelled) setCheckingUsing(false);
      }
    };

    checkUsing();
    return () => {
      cancelled = true;
    };
  }, [courseId, loginUserId]);

  const isOwner = course && String(course.userId) === String(loginUserId);
  const rawPublic = course ? course.isPublic ?? course.public : 0;
  const isPublic = rawPublic === 1 || rawPublic === "1" || rawPublic === true;

  const showPublicBtn = isOwner && !isPublic;
  const showUseBtn = isPublic || isOwner;

  const handleTogglePublic = async () => {
    if (!window.confirm("공개로 전환하시겠습니까? (되돌릴 수 없음)")) return;
    try {
      await publicCourse(course.courseId, loginUserId);
      // [변경] alert -> showToast ('i'nfo)
      showToast("공개 코스로 전환되었습니다.", "i");
      fetchCourseDetail();
    } catch (e) {
      // [변경] alert -> showToast ('e'rror)
      showToast("전환에 실패했습니다.", "e");
    }
  };

  const handleUseCourse = async () => {
    if (!course) return;

    // 1) 진행중이 있으면 이어하기 (새로 시작 API 호출 금지)
    if (usingWorkRecordId) {
      showToast("진행 중인 운동을 이어서 진행합니다.", "i");
      navigate("/course/using", {
        state: {
          course,
          workRecordId: usingWorkRecordId,
          userId: loginUserId,
        },
      });
      return;
    }

    // 2) 진행중이 없으면 기존 로직(시작하기)
    try {
      const resp = await startCourseApi(course.courseId, loginUserId);
      if (!resp || !resp.workRecordId) {
        showToast("운동기록 시작 정보가 없습니다.", "e");
        return;
      }
      showToast("운동을 시작합니다.", "i");
      navigate("/course/using", {
        state: {
          course: resp.course || course,
          workRecordId: resp.workRecordId,
          userId: loginUserId,
        },
      });
    } catch (e) {
      console.error(e);
      showToast("운동 시작에 실패했습니다.", "e");
    }
  };

  const handleWriteReview = async () => {
    if (!course) return;
    try {
      const resp = await checkAddReviewPermission(course.courseId, loginUserId);
      if (!resp || resp.allowed === false) {
        // [변경] 권한 없음 등은 Warning
        showToast(resp?.message || "후기를 작성할 수 없습니다.", "w");
        return;
      }
      if (!resp.workRecordId) {
        showToast("작성 가능한 운동기록 정보가 없습니다.", "w");
        return;
      }
      navigate(`/course/${course.courseId}/reviews/add`, {
        state: {
          mode: "create",
          courseId: course.courseId,
          workRecordId: resp.workRecordId,
          loginUserId,
        },
      });
    } catch (e) {
      console.error(e);
      showToast("후기 작성 권한 확인 중 오류가 발생했습니다.", "e");
    }
  };

  const handleDeleteReview = async (review) => {
    if (!window.confirm("후기를 삭제하시겠습니까?")) return;
    try {
      const reviewId = review.reviewId || review.id;
      await deleteReviewApi(course.courseId, reviewId, loginUserId);
      setReviews((prev) =>
        prev.filter((r) => (r.reviewId || r.id) !== reviewId)
      );
      // [변경] alert -> showToast
      showToast("후기가 삭제되었습니다.", "i");
    } catch (e) {
      showToast("후기 삭제에 실패했습니다.", "e");
    }
  };

  const handleClickReview = async (review) => {
    if (!course) return;
    const reviewId = review.reviewId || review.id;
    try {
      const resp = await checkUpdateReviewPermission(
        course.courseId,
        reviewId,
        loginUserId
      );
      if (!resp || resp.allowed === false) {
        showToast(resp?.message || "후기를 수정할 수 없습니다.", "w");
        return;
      }
      navigate(`/course/${course.courseId}/reviews/${reviewId}/edit`, {
        state: { mode: "edit", courseId: course.courseId, review, loginUserId },
      });
    } catch (e) {
      console.error(e);
      showToast("후기 수정 권한 확인 중 오류가 발생했습니다.", "e");
    }
  };

  if (!courseId)
    return (
      <div className="p-10 text-center text-gray-500">잘못된 접근입니다.</div>
    );

  return (
    <div className="h-screen overflow-hidden bg-gray-50 flex flex-col max-w-md mx-auto relative">
      <header className="px-5 pt-6 pb-4 bg-white sticky top-0 z-20 flex items-center justify-between shadow-sm">
        <div className="w-8">
          <BackButton />
        </div>
        <h1 className="text-2xl font-bold text-gray-800">코스 상세</h1>
        <div className="w-8" />
      </header>

      <main className="flex-1 overflow-y-auto px-5 py-6 pb-24 scrollbar-hide space-y-6">
        {loading && <div className="text-center text-gray-500">로딩중...</div>}

        <section className="bg-white rounded-[24px] overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100">
          <div className="relative h-64 w-full">
            <CourseMapView
              pathData={course?.coursePathList}
              startLocation={course?.startPoint}
            />
          </div>
        </section>

        {course && (
          <CourseDetailHeader
            name={course.courseName}
            distance={course.distance}
            startLocation={course.startLocation || "출발지 정보 없음"}
            type={course.type}
            ratingAvg={course.ratingAvg || 0}
            showPublicBtn={showPublicBtn}
            showUseBtn={showUseBtn}
            onTogglePublic={handleTogglePublic}
            onUseCourse={handleUseCourse}
            onWriteReview={handleWriteReview}
            useButtonText={
              checkingUsing
                ? "확인중..."
                : usingWorkRecordId
                ? "이어하기"
                : "코스 시작하기"
            }
          />
        )}

        <section className="space-y-4">
          <div className="flex items-center justify-between px-1">
            <h2 className="text-lg font-bold text-gray-900">
              후기 ({reviews.length})
            </h2>
            <span className="text-xs text-gray-400">
              {isPublic ? "공개 코스" : "비공개 코스"}
            </span>
          </div>

          {reviews.length === 0 && (
            <div className="text-center py-10 text-gray-400 bg-white rounded-[24px] border border-gray-100">
              작성된 후기가 없습니다.
            </div>
          )}

          {reviews.map((review) => {
            const isReviewOwner = String(review.userId) === String(loginUserId);
            const imageList = Array.isArray(review.reviewImages)
              ? review.reviewImages
              : [];

            return (
              <article
                key={review.reviewId}
                className="bg-white p-5 rounded-[24px] shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100 space-y-3"
              >
                <ReviewHeader
                  review={review} /* 👈 review 객체를 통째로 넘깁니다 */
                  isOwner={isReviewOwner}
                  onEdit={() => handleClickReview(review)}
                  onDelete={() => handleDeleteReview(review)}
                />

                <ReviewContent value={review.reviewContent} readOnly />

                {imageList.length > 0 && (
                  <div className="mt-2 flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
                    {imageList.map((img) => (
                      <img
                        key={img.reviewImageId}
                        src={img.image}
                        alt="후기"
                        className="w-20 h-20 rounded-xl object-cover border border-gray-100"
                      />
                    ))}
                  </div>
                )}
              </article>
            );
          })}
        </section>
      </main>
    </div>
  );
}

export default GetCoursePage;
